/**
 * @fileoverview Hook for managing font loading state, including custom fonts like Gelica and Inter
 *
 * @example
 * // Usage:
 * import { useFonts } from './useFonts'
 *
 * function Component() {
 *   const areFontsLoaded = useFonts()
 *
 *   if (!areFontsLoaded) {
 *     return <LoadingSpinner />
 *   }
 *
 *   return <div>Content with custom fonts</div>
 * }
 */


import { useEffect, useState } from 'react'
import config from '../config'

/**
 * @function useFonts
 * @description Hook for loading custom fonts
 * @returns {boolean} True if fonts are loaded, false otherwise
 */
export function useFonts(): boolean {
    const [areFontsLoaded, setAreFontsLoaded] = useState<boolean>(false)

    useEffect(() => {
        const fonts: FontFace[] = [
            new FontFace(
                'Gelica W01 SemiBold',
                `url(${config.s3.getStaticURL('08b919ee-494b-4a67-a796-d7932290f8de.woff2')})`
            ),
            new FontFace(
                'Inter',
                `url(${config.s3.getStaticURL('Inter+Web/Inter.var.woff2')})`,
                {}
            ),
            new FontFace('Inter', `url(${config.s3.getStaticURL('Inter+Web/Inter-Light.woff2')})`, {
                weight: '300',
            }),
            new FontFace(
                'Inter',
                `url(${config.s3.getStaticURL('Inter+Web/Inter-Regular.woff2')})`,
                {
                    weight: '400',
                }
            ),
            new FontFace(
                'Inter',
                `url(${config.s3.getStaticURL('Inter+Web/Inter-Medium.woff2')})`,
                {
                    weight: '500',
                }
            ),
            new FontFace(
                'Inter',
                `url(${config.s3.getStaticURL('Inter+Web/Inter-SemiBold.woff2')})`,
                {
                    weight: '600',
                }
            ),
        ]

        Promise.all(fonts.map((font) => font.load()))
            .then((fonts) => {
                fonts.forEach((font) => document.fonts.add(font))
            })
            .finally(() => {
                setAreFontsLoaded(true)
            })
    }, [])

    return areFontsLoaded
}
